import React, { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, IconButton, Tabs, Tab } from "@mui/material";
import QRCode from "qrcode.react";
import { X, Printer, Copy } from "lucide-react";

export default function QrCodeDialog({
  open,
  onClose,
  title,
  qrValue,
  qrItems,
  qrSize = 200,
  printTitle,
  showCopyButton = true,
  showPrintButton = true,
  headerActions,
  children,
  actions,
  width = 440,
}) {
  const classes = useStyles();
  const qrRef = useRef();
  const [toastOpen, setToastOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const isMulti = Array.isArray(qrItems) && qrItems.length > 0;
  const activeItem = isMulti ? qrItems[selectedTab] : null;
  const activeQrValue = isMulti ? activeItem?.value : qrValue;
  const activePrintTitle = isMulti
    ? `${title} — ${activeItem?.label}`
    : (printTitle || title);

  const handlePrint = useCallback(() => {
    const content = qrRef.current;
    if (!content) return;
    const printWindow = window.open("", "_blank", "width=800,height=600");

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .print-source { height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; }
            @media print {
              body { margin: 0; padding: 0; }
              .print-source { width: 100%; }
            }
          </style>
        </head>
        <body>
          <div class="print-source">
            <h1 style="text-align: center;">${activePrintTitle}</h1>
            ${content.innerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  }, [activePrintTitle]);

  const handleCopy = useCallback(() => {
    if (!activeQrValue) return;
    navigator.clipboard.writeText(activeQrValue);
    setToastOpen(true);
  }, [activeQrValue]);

  const handleCloseToast = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  }, []);

  return (
    <Dialog
      className={classes.dialog}
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="lg"
      PaperProps={{ style: { width: `${width}px` } }}
    >
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title}>{title}</DialogTitle>
        <div className={classes.head__buttonContainer}>
          {headerActions}
          {showCopyButton && activeQrValue && (
            <button className={classes.iconButton} onClick={handleCopy} title="Copy link">
              <Copy size={16} />
            </button>
          )}
          {showPrintButton && activeQrValue && (
            <button className={classes.iconButton} onClick={handlePrint} title="Print QR code">
              <Printer size={16} />
            </button>
          )}
          <button onClick={onClose} className={classes.dialog__closeButton}>
            <X size={16} />
          </button>
        </div>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {isMulti && (
            <Tabs
              value={selectedTab}
              onChange={(e, newVal) => setSelectedTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              className={classes.tabs}
              TabIndicatorProps={{ style: { backgroundColor: "var(--color-primary)" } }}
            >
              {qrItems.map((item, index) => (
                <Tab
                  key={item.key ?? index}
                  label={item.label}
                  className={classes.tab}
                  sx={{
                    "&.Mui-selected": { color: "var(--color-primary)" },
                    textTransform: "none",
                    fontFamily: "Lato, sans-serif",
                  }}
                />
              ))}
            </Tabs>
          )}
          {activeQrValue && (
            <div className={classes.qrContainer}>
              <div
                className="print-source"
                ref={qrRef}
                style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
              >
                <QRCode id="Qr" value={activeQrValue} bgColor="#ffffff" renderAs="svg" size={qrSize} />
                <div style={{ width: `${qrSize}px`, display: "flex", justifyContent: "center", marginTop: "8px" }}>
                  Powered by Flagstone
                </div>
              </div>
            </div>
          )}
          {isMulti && activeItem?.description && (
            <p className={classes.itemDescription}>{activeItem.description}</p>
          )}
          {children}
        </div>
      </DialogContent>
      {actions && (
        <DialogActions sx={{ display: "flex", justifyContent: "flex-end", px: "16px", pb: "16px" }}>
          {actions}
        </DialogActions>
      )}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        message="Link copied to clipboard"
        action={
          <IconButton size="small" color="inherit" onClick={handleCloseToast}>
            <X fontSize="small" />
          </IconButton>
        }
      />
    </Dialog>
  );
}

QrCodeDialog.propTypes = {
  /** Whether the dialog is open */
  open: PropTypes.bool.isRequired,
  /** Callback fired when the dialog requests to be closed */
  onClose: PropTypes.func.isRequired,
  /** Dialog title displayed in the header */
  title: PropTypes.string.isRequired,
  /** The URL/value encoded in the QR code (single QR mode) */
  qrValue: PropTypes.string,
  /** Array of QR items for multi-QR mode: [{ key?, label, value, description? }] */
  qrItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  /** QR code size in pixels (default: 200) */
  qrSize: PropTypes.number,
  /** Title shown on the printed page — single mode only (defaults to title) */
  printTitle: PropTypes.string,
  /** Show the copy-to-clipboard button (default: true) */
  showCopyButton: PropTypes.bool,
  /** Show the print button (default: true) */
  showPrintButton: PropTypes.bool,
  /** Additional action buttons rendered in the header before copy/print/close */
  headerActions: PropTypes.node,
  /** Content rendered below the QR code */
  children: PropTypes.node,
  /** Content rendered in the DialogActions footer area */
  actions: PropTypes.node,
  /** Dialog width in pixels (default: 440) */
  width: PropTypes.number,
};

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",
    lineHeight: "100% !important",
    padding: "0px !important",
  },

  head__buttonContainer: {
    display: "flex",
    gap: "8px",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    height: "24px !important",
    width: "24px !important",
    borderRadius: "3px",
    transition: "all 0.2s",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px !important",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "var(--color-primary) auto 1px",
    },
  },

  iconButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    height: "24px !important",
    width: "24px !important",
    borderRadius: "3px",
    transition: "all 0.2s",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px !important",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },

    "&:focus-visible": {
      outline: "var(--color-primary) auto 1px",
    },
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    margin: "0px !important",
  },

  qrContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
    padding: "24px 16px",
  },

  tabs: {
    width: "100%",
    marginBottom: "16px",
    borderBottom: "1px solid #EDEDED",
  },

  tab: {
    minWidth: "auto !important",
    padding: "8px 16px !important",
    fontSize: "14px !important",
  },

  itemDescription: {
    marginTop: "12px",
    fontSize: "14px",
    color: "#666",
    textAlign: "center",
    width: "100%",
  },
}));
